/**
 * Anti-clickjacking en runtime (plan 7.3.6, capa 2).
 *
 * La capa 1 es la CSP dinamica con `frame-ancestors` del tenant que emite el NGINX
 * del widget via `auth_request`. Esta capa 2 vuelve a verificar en el propio widget,
 * contra `checkout.allowed_origins` del intent, antes de montar cualquier flujo de
 * pasarela. El origen validado es ademas el `targetOrigin` de todos los postMessage:
 * aunque un atacante lograra embeber el iframe, los mensajes no le llegarian.
 */

/** Subconjunto estructural de `DOMStringList` que necesita el guard. */
export interface AncestorOriginsLike {
  readonly length: number;
  item(index: number): string | null;
}

/** Contexto de embebido, inyectable para poder testear sin un iframe real. */
export interface FrameContext {
  readonly isTopLevel: boolean;
  readonly ancestorOrigins?: AncestorOriginsLike | undefined;
  readonly referrer: string;
}

export class EmbedOriginDeniedError extends Error {
  readonly code = 'embed_origin_denied';

  constructor(message: string) {
    super(message);
    this.name = 'EmbedOriginDeniedError';
  }
}

/** Lee el contexto real del navegador. */
export function currentFrameContext(win: Window = window): FrameContext {
  // Firefox no implementa location.ancestorOrigins: el tipo de lib.dom miente y hay
  // que tratarlo como opcional.
  const ancestorOrigins = win.location.ancestorOrigins as AncestorOriginsLike | undefined;
  const base = {
    isTopLevel: win.self === win.top,
    referrer: win.document.referrer,
  };
  return ancestorOrigins === undefined ? base : { ...base, ancestorOrigins };
}

function normalizeOrigin(value: string | null): string | null {
  if (value === null || value.length === 0 || value === 'null') {
    return null;
  }
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

/**
 * Origen del documento top-level que nos embebe, o null si no puede determinarse.
 * Se toma el ULTIMO elemento de ancestorOrigins (el top real, no un iframe
 * intermedio); si el navegador no expone la lista se cae al origen del referrer.
 */
export function detectEmbedderOrigin(context: FrameContext = currentFrameContext()): string | null {
  const ancestors = context.ancestorOrigins;
  if (ancestors !== undefined && ancestors.length > 0) {
    return normalizeOrigin(ancestors.item(ancestors.length - 1));
  }
  return normalizeOrigin(context.referrer);
}

/**
 * Verifica que el widget este embebido desde un origen registrado del tenant.
 *
 * @returns el origen embebedor normalizado, que pasa a ser el targetOrigin del canal
 * @throws EmbedOriginDeniedError si el widget corre top-level, si no se puede
 *   determinar el embebedor o si el origen no esta en `allowed_origins`
 */
export function assertEmbeddedFromAllowedOrigin(
  allowedOrigins: string[],
  context: FrameContext = currentFrameContext(),
): string {
  if (context.isTopLevel) {
    // El widget jamas opera top-level: sin embebedor no hay canal postMessage valido.
    throw new EmbedOriginDeniedError(
      'El checkout solo funciona embebido via @apipay/checkout-js',
    );
  }

  const embedder = detectEmbedderOrigin(context);
  if (embedder === null) {
    throw new EmbedOriginDeniedError('No se pudo determinar el origen embebedor del checkout');
  }

  // Lista vacia = fail closed: sin origenes registrados no se autoriza a nadie.
  const allowed = allowedOrigins
    .map((origin) => normalizeOrigin(origin))
    .filter((origin): origin is string => origin !== null);

  if (!allowed.includes(embedder)) {
    throw new EmbedOriginDeniedError('Origen embebedor no autorizado para este tenant');
  }

  return embedder;
}
